import { useCallback, useEffect, useRef } from "react";

// Kéo mục menu đang mở vào tầm nhìn của cột sidebar.
//
// Menu dài hơn màn hình ở gần hết các vai, nên khi tải lại trang (hoặc mở
// thẳng một link sâu) mục đang mở thường nằm khuất phía dưới: người dùng thấy
// một cột menu mà không mục nào sáng, tưởng mình lạc trang.
//
// Chỉ cuộn khi mục đang KHUẤT. Mục đã nằm trong tầm nhìn thì giữ nguyên vị trí
// cột — cuộn lại (dù để canh giữa) làm menu giật mỗi lần chuyển trang.

/** Element đánh dấu mục đang mở; xem activeProps trong SidebarContent. */
export const ACTIVE_NAV_SELECTOR = "[data-nav-active]";

/**
 * Cuộn `scroller` vừa đủ để mục đang mở lọt vào giữa khung.
 *
 * Cuộn tức thì chứ không mượt: cảnh dùng chính là lúc trang vừa tải, ở đó hiệu
 * ứng trượt chỉ là chuyển động thừa (và phải né `prefers-reduced-motion`).
 */
export function scrollActiveNavIntoView(scroller: HTMLElement | null): void {
  const active = scroller?.querySelector<HTMLElement>(ACTIVE_NAV_SELECTOR);
  if (!scroller || !active) return;

  const view = scroller.getBoundingClientRect();
  const item = active.getBoundingClientRect();
  if (item.top >= view.top && item.bottom <= view.bottom) return; // đang thấy rồi

  const offsetInView = item.top - view.top; // vị trí mục so với mép trên khung
  const centered = scroller.scrollTop + offsetInView - (scroller.clientHeight - item.height) / 2;
  const max = scroller.scrollHeight - scroller.clientHeight;
  scroller.scrollTo({ top: Math.min(Math.max(0, centered), Math.max(0, max)), behavior: "auto" });
}

/**
 * Ref gắn vào phần tử cuộn của cột menu; tự kéo mục đang mở vào tầm nhìn.
 *
 * Trả về CALLBACK ref chứ không phải object ref, vì hai lúc cần cuộn là hai
 * thời điểm khác nhau:
 *
 *  - lúc phần tử vừa gắn vào DOM — callback ref chạy đúng khoảnh khắc đó. Với
 *    ngăn kéo mobile thì đây là lối duy nhất: Radix dựng panel trong portal,
 *    nên `useEffect` của component cha chạy khi `ref.current` VẪN CÒN null
 *    (đã đo: mở ngăn kéo mà không lần `scrollTo` nào được gọi).
 *  - lúc đổi trang mà phần tử vẫn đang gắn (cột menu desktop) — việc của
 *    `useEffect` theo `key`.
 *
 * Phép tính chỉ đọc HIỆU `item.top - view.top`, nên hiệu ứng trượt vào của
 * ngăn kéo (một `transform` dịch cả khối) không làm sai kết quả.
 */
export function useScrollActiveNavIntoView<T extends HTMLElement>(key: string) {
  const node = useRef<T | null>(null);
  const setNode = useCallback((el: T | null) => {
    node.current = el;
    if (el) scrollActiveNavIntoView(el);
  }, []);
  useEffect(() => {
    scrollActiveNavIntoView(node.current);
  }, [key]);
  return setNode;
}
